/**
 * Calendário das despesas e receitas fixas (recorrências). Funções puras sobre datas
 * "AAAA-MM-DD" (dias de calendário, sem fuso): quem decide o "hoje" é o fuso da loja.
 *
 * Regras:
 * - Frequências por mês (mensal, bimestral...) vencem no `dayOfMonth`; se o mês não tem esse
 *   dia, vence no último dia do mês (31 em fevereiro = 28 ou 29). Sem `dayOfMonth`, o dia é o
 *   da data de início.
 * - Semanal: a cada 7 dias a partir do início.
 * - A primeira ocorrência é a primeira data no calendário da regra que não é anterior ao início.
 */

export const RECURRENCE_FREQUENCIES = ['weekly', 'monthly', 'bimonthly', 'quarterly', 'semiannual', 'yearly'] as const;
export type RecurrenceFrequencyKey = (typeof RECURRENCE_FREQUENCIES)[number];

export const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequencyKey, string> = {
  weekly: 'Semanal',
  monthly: 'Mensal',
  bimonthly: 'Bimestral',
  quarterly: 'Trimestral',
  semiannual: 'Semestral',
  yearly: 'Anual',
};

/** Passo em meses de cada frequência (semanal não é por mês). */
export const RECURRENCE_MONTH_STEP: Record<RecurrenceFrequencyKey, number | null> = {
  weekly: null,
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannual: 6,
  yearly: 12,
};

/** Quanto uma ocorrência pesa por mês (semanal = 52 semanas / 12 meses). */
export const RECURRENCE_MONTHLY_FACTOR: Record<RecurrenceFrequencyKey, number> = {
  weekly: 52 / 12,
  monthly: 1,
  bimonthly: 1 / 2,
  quarterly: 1 / 3,
  semiannual: 1 / 6,
  yearly: 1 / 12,
};

export function monthlyEquivalentCents(amountCents: number, frequency: RecurrenceFrequencyKey): number {
  return Math.round(amountCents * RECURRENCE_MONTHLY_FACTOR[frequency]);
}

function parts(key: string): [number, number, number] {
  return key.split('-').map(Number) as [number, number, number];
}

function fmt(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Último dia do mês (m = 1..12). */
export function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function addDays(key: string, days: number): string {
  const [y, m, d] = parts(key);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Data no mês (y, m + offset) com o dia `anchor` limitado ao fim do mês. */
function monthDate(y: number, m: number, offset: number, anchor: number): string {
  const idx = y * 12 + (m - 1) + offset;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  return fmt(ny, nm, Math.min(anchor, lastDayOfMonth(ny, nm)));
}

export interface RecurrenceCalendar {
  frequency: RecurrenceFrequencyKey;
  /** Dia do vencimento (1..31) nas frequências por mês. */
  dayOfMonth: number | null;
  startDate: string;
}

/** Dia âncora: o `dayOfMonth` ou o dia da data de início. */
export function anchorDay(rule: Pick<RecurrenceCalendar, 'dayOfMonth' | 'startDate'>): number {
  return rule.dayOfMonth ?? parts(rule.startDate)[2];
}

/** Primeira ocorrência (>= início). */
export function firstOccurrence(rule: RecurrenceCalendar): string {
  const step = RECURRENCE_MONTH_STEP[rule.frequency];
  if (step === null) return rule.startDate;
  const [y, m] = parts(rule.startDate);
  const anchor = anchorDay(rule);
  const same = monthDate(y, m, 0, anchor);
  return same >= rule.startDate ? same : monthDate(y, m, step, anchor);
}

/** Ocorrência seguinte a `current` (que precisa ser uma data do calendário da regra). */
export function nextOccurrence(rule: RecurrenceCalendar, current: string): string {
  const step = RECURRENCE_MONTH_STEP[rule.frequency];
  if (step === null) return addDays(current, 7);
  const [y, m] = parts(current);
  return monthDate(y, m, step, anchorDay(rule));
}

/** Primeira ocorrência da regra que não é anterior a `from`. */
export function firstOccurrenceOnOrAfter(rule: RecurrenceCalendar, from: string): string {
  let due = firstOccurrence(rule);
  for (let guard = 0; due < from && guard < 5000; guard++) due = nextOccurrence(rule, due);
  return due;
}

/**
 * Ocorrências a partir de `from` (uma data do calendário da regra) até `to` (inclusive),
 * respeitando a data final. Usado pela geração e pela projeção do fluxo de caixa.
 */
export function occurrencesUntil(rule: RecurrenceCalendar & { endDate: string | null }, from: string, to: string, max = 500): string[] {
  const out: string[] = [];
  let due = from;
  while (due <= to && (rule.endDate === null || due <= rule.endDate) && out.length < max) {
    out.push(due);
    due = nextOccurrence(rule, due);
  }
  return out;
}
